/**
 * "Abrir o original" (E184.F4.T01, GUS-145 item 3, ADR-0136 §4): o arquivo recebido numa aba nova,
 * no visualizador do navegador, para selecionar e copiar o texto do PDF e colar no editor. Na
 * página da fonte (ficha e conferência da leitura) e no documento aberto na mesa.
 */
import type { AxiosInstance } from 'axios';
import { useState } from 'react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { abrirOriginalEmAbaNova } from '@/lib/abrir_original';
import { DocumentError } from '@/lib/case_source_documents';

const t = ptBR.fichaDaLeitura.original;

export interface AbrirOriginalProps {
  client: AxiosInstance;
  caseId: string;
  documentoId: string;
  nome: string;
}

export function AbrirOriginal({
  client,
  caseId,
  documentoId,
  nome,
}: AbrirOriginalProps): JSX.Element {
  const [abrindo, setAbrindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function abrir(): Promise<void> {
    setAbrindo(true);
    setErro(null);
    try {
      await abrirOriginalEmAbaNova(client, caseId, documentoId);
    } catch (err) {
      const divergente = err instanceof DocumentError && err.reason === 'original_divergente';
      setErro(divergente ? t.divergente : t.erro);
    } finally {
      setAbrindo(false);
    }
  }

  return (
    <div className="space-y-sc-1" data-testid="abrir-original">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => void abrir()}
        disabled={abrindo}
        aria-label={t.abrirO.replace('{documento}', nome)}
      >
        {abrindo ? t.abrindo : t.abrir}
      </Button>
      <p className="text-caption text-fg-muted">{t.dica}</p>
      {erro ? (
        <p role="alert" className="text-caption text-danger-700" data-testid="abrir-original-erro">
          {erro}
        </p>
      ) : null}
    </div>
  );
}
